/**
 * Infrastructure verdict engine — V105-4-1 / ADR-0072 §3 (D-105-9, PRD §5.2).
 *
 * PURE: no I/O, no clock, no randomness. `evaluateInfraVerdict(item, answers, ctx)` turns the
 * five recorded answers of one inventory item into one verdict:
 *
 *   out_of_scope            R1  Q1 = no ∧ Q2 = no
 *   partition               R2  Q1 = yes ∧ an approved partition pattern whose RESIDUAL verdict
 *                               (R1 / R3a / R3b / R4 rerun on the pattern's residual Q1/Q2, Q3
 *                               recomputed) is Out of scope or Documented third party
 *   replace                 R3a Q3.status = unmet
 *                           R3b Q1 = yes ∧ function ∈ CORE ∧ Q3 not required ∧ precedent < precedent_min
 *   documented_third_party  R4  otherwise (subtype certified | non_certified)
 *   pending                 R0  a required input is missing (`missing[]` names each)
 *
 * Rules are evaluated in that order; the first match wins. Only CONFIRMED answers may be
 * passed in (a suggestion never counts; the caller filters). Q4 and Q5's TPR completeness
 * never change a verdict: they raise findings (`infra_evidence_gap`, `infra_tpr_gap`) on an
 * item whose effective verdict is Documented third party (for a Partition, the residual's).
 *
 * `computeInfraVerdict` adds `inputs_sha256`: SHA-256 over the canonical JSON of exactly the
 * inputs the rules read (item identity + function + certified flag, each answer's value and
 * provenance, the target level, the patterns, the rule set). Snapshots pin it (ADR-0072 §8).
 *
 * The rule-set values live here as reviewed code, versioned `infra-verdict-rules@1`
 * (`precedent_min` = 1, ADR-0072 open question 4 default); a change is a new version.
 */
import { canonicalJson, sha256Hex } from '../utils/audit-crypto.js';
export const INFRA_VERDICT_RULE_SET = 'infra-verdict-rules@1';
export const INFRA_VERDICT_RULES_V1 = Object.freeze({ version: INFRA_VERDICT_RULE_SET, precedent_min: 1 });
export const INFRA_VERDICTS = ['pending', 'out_of_scope', 'documented_third_party', 'partition', 'replace'];
/** ADR-0072 §3 function sets. */
export const HOSTING_FUNCTIONS = new Set(['primary_data_store', 'secondary_data_store', 'compute']);
export const CORE_FUNCTIONS = new Set([...HOSTING_FUNCTIONS, 'identity']);
/** Required inputs (ADR-0072 §3). Order: function, q1, q2, q3, q5.precedent_count, conflict. */
function missingInputs(fn, a) {
    const out = [];
    const q1 = a.q1?.value;
    if (!fn)
        out.push('function');
    if (!q1) {
        out.push('q1');
        return out;
    }
    const q2 = a.q2?.value;
    if (q1 === 'no' && !q2)
        out.push('q2');
    const needQ3 = q1 !== 'no' || q2 === 'yes';
    const q3 = a.q3?.value;
    if (needQ3 && !q3)
        out.push('q3');
    if (q3 && fn && q1 === 'yes' && CORE_FUNCTIONS.has(fn) && q3.status === 'not_required') {
        const n = a.q5?.value?.precedent_count;
        if (typeof n !== 'number')
            out.push('q5.precedent_count');
    }
    if (q3 && needQ3 && q3.required && q3.coverage?.state === 'conflict')
        out.push('q3.coverage_conflict');
    return out;
}
/** Q3 for residual answers: `required` follows the residual Q1; coverage stays (ADR-0072 §3 R2). */
function recomputeQ3(q3, q1, fn) {
    const required = (q1 === 'yes' || q1 === 'metadata_only') && HOSTING_FUNCTIONS.has(fn);
    const covered = !!q3.coverage?.covered;
    const status = !required ? 'not_required' : covered ? 'satisfiable' : 'unmet';
    return { ...q3, required, status, unmet_objective_ids: status === 'unmet' ? [...(q3.required_objective_ids ?? q3.unmet_objective_ids ?? [])] : [] };
}
/** R1, R3a, R3b, R4 over complete inputs (R2 is the caller's). Null when inputs are missing. */
function evaluateCore(item, a, rules) {
    const missing = missingInputs(item.function, a);
    if (missing.length)
        return null;
    const fn = item.function;
    const q1 = a.q1.value;
    const q2 = a.q2?.value ?? null;
    const q3 = a.q3?.value ?? null;
    if (q1 === 'no' && q2 === 'no')
        return { verdict: 'out_of_scope', subtype: null, rule: 'R1', reasons: ['OOS_NO_FCD_NO_CIA'], missing: [], unmet_objective_ids: [] };
    if (q3 && q3.status === 'unmet') {
        return { verdict: 'replace', subtype: null, rule: 'R3a', reasons: ['REPLACE_INHERITANCE_UNMET'], missing: [], unmet_objective_ids: [...(q3.unmet_objective_ids ?? [])].sort() };
    }
    const precedent = a.q5?.value?.precedent_count;
    if (q1 === 'yes' && CORE_FUNCTIONS.has(fn) && q3?.status === 'not_required' && typeof precedent === 'number' && precedent < rules.precedent_min) {
        return { verdict: 'replace', subtype: null, rule: 'R3b', reasons: ['REPLACE_CORE_PATH_NO_PRECEDENT'], missing: [], unmet_objective_ids: [] };
    }
    const reasons = [item.certified ? 'DOC_CERTIFIED' : 'DOC_NON_CERTIFIED'];
    if (!item.certified && item.offering_authorized)
        reasons.push('DOC_PRODUCT_NOT_LISTED');
    if (q3?.coverage?.covered && !q3.coverage.current_offering_covered)
        reasons.push('DOC_OFFERING_MOVE');
    if (q1 === 'yes' && precedent === 0)
        reasons.push('DOC_NO_PRECEDENT');
    return { verdict: 'documented_third_party', subtype: item.certified ? 'certified' : 'non_certified', rule: 'R4', reasons, missing: [], unmet_objective_ids: [] };
}
function residualAnswers(a, p, fn) {
    const q1 = { value: p.residual.q1, provenance: 'attested' };
    const q2 = p.residual.q1 === 'no' && p.residual.q2 ? { value: p.residual.q2, provenance: 'attested' } : null;
    return { ...a, q1, q2, q3: a.q3 ? { ...a.q3, value: recomputeQ3(a.q3.value, p.residual.q1, fn) } : a.q3 };
}
function findingsFor(effective, a) {
    if (effective !== 'documented_third_party')
        return [];
    const out = [];
    const q4 = a.q4?.value;
    if (q4 && (q4.status === 'partial' || q4.status === 'none')) {
        out.push({ kind: 'infra_evidence_gap', rule_ref: 'KSI-CNA-IBP, KSI-SCR-MON', detail: q4.status === 'none' ? 'No connector collects evidence for this service.' : 'A connector observes the provider, but no KSI test reads this product.' });
    }
    const q5 = a.q5?.value;
    if (q5 && q5.tpr_missing?.length)
        out.push({ kind: 'infra_tpr_gap', rule_ref: 'MAS-CSO-TPR', detail: `Missing: ${[...q5.tpr_missing].join(', ')}.` });
    return out;
}
function labelsFor(a) {
    const out = [];
    if (a.q3?.provenance === 'attested')
        out.push('q3_override');
    if (a.q4?.provenance === 'attested')
        out.push('q4_attested');
    if (a.q3?.value?.required && a.q3.value.unverified_objective_ids?.length)
        out.push('unverified_objectives');
    if (a.q3?.value?.catalog_state === 'awaiting_review')
        out.push('catalog_awaiting_review');
    if (a.q3?.value?.coverage?.state === 'conflict')
        out.push('coverage_conflict');
    return out;
}
const VERDICT_RANK = { out_of_scope: 0, documented_third_party: 1, partition: 2, replace: 3, pending: 4 };
/** The verdict for one item (synchronous; no hash). */
export function evaluateInfraVerdict(item, answers, ctx) {
    const rules = ctx.rules ?? INFRA_VERDICT_RULES_V1;
    const base = {
        ref: item.ref, item_id: item.item_id ?? null, target_impact_level: ctx.target_impact_level, rule_set: rules.version, labels: labelsFor(answers),
    };
    const missing = missingInputs(item.function, answers);
    if (missing.length) {
        return {
            ...base, verdict: 'pending', subtype: null, rule: 'R0', reasons: missing.map((m) => `PENDING_INPUT:${m}`), missing,
            partition_pattern_id: null, residual: null, unmet_objective_ids: [], findings: [],
        };
    }
    const fn = item.function;
    // R1 first (rule order), then R2.
    const core = evaluateCore(item, answers, rules);
    if (core.rule !== 'R1' && answers.q1.value === 'yes') {
        const eligible = [...(ctx.partition_patterns ?? [])]
            .map((p) => {
            const ra = residualAnswers(answers, p, fn);
            const r = evaluateCore(item, ra, rules);
            return r && (r.verdict === 'out_of_scope' || r.verdict === 'documented_third_party') ? { p, ra, r } : null;
        })
            .filter((x) => !!x)
            .sort((x, y) => VERDICT_RANK[x.r.verdict] - VERDICT_RANK[y.r.verdict] || (x.p.id < y.p.id ? -1 : x.p.id > y.p.id ? 1 : 0));
        const best = eligible[0];
        if (best) {
            return {
                ...base, verdict: 'partition', subtype: null, rule: 'R2',
                reasons: [`PARTITION_PATTERN:${best.p.id}`, `RESIDUAL:${best.r.verdict}`],
                missing: [], partition_pattern_id: best.p.id,
                residual: { q1: best.ra.q1.value, q2: best.ra.q2?.value ?? null, verdict: best.r.verdict, rule: best.r.rule, reasons: best.r.reasons },
                unmet_objective_ids: [], findings: findingsFor(best.r.verdict, best.ra),
            };
        }
    }
    return { ...base, ...core, partition_pattern_id: null, residual: null, findings: findingsFor(core.verdict, answers) };
}
/** Exactly the inputs the rules read, canonical (what `inputs_sha256` covers). */
export function verdictInputs(item, answers, ctx) {
    const ans = (q) => {
        const x = answers[q];
        return x ? { value: x.value, provenance: x.provenance } : null;
    };
    return {
        item: { ref: item.ref, function: item.function, certified: item.certified, ...(item.offering_authorized && !item.certified ? { offering_authorized: true } : {}) },
        answers: { q1: ans('q1'), q2: ans('q2'), q3: ans('q3'), q4: ans('q4'), q5: ans('q5') },
        target_impact_level: ctx.target_impact_level,
        partition_patterns: [...(ctx.partition_patterns ?? [])].map((p) => ({ id: p.id, residual: { q1: p.residual.q1, q2: p.residual.q2 ?? null } }))
            .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
        rules: ctx.rules ?? INFRA_VERDICT_RULES_V1,
    };
}
/** `evaluateInfraVerdict` plus `inputs_sha256` (ADR-0072 §3 signature). */
export async function computeInfraVerdict(item, answers, ctx) {
    const result = evaluateInfraVerdict(item, answers, ctx);
    return { ...result, inputs_sha256: await sha256Hex(canonicalJson(verdictInputs(item, answers, ctx))) };
}
/** Short customer-facing label per verdict (the board's chrome). */
export const INFRA_VERDICT_LABEL = Object.freeze({
    pending: 'Needs answers', out_of_scope: 'Out of scope', documented_third_party: 'Documented third party', partition: 'Partition', replace: 'Replace',
});
//# sourceMappingURL=infra-verdict.js.map